// src/review/canvas/shared/EditableImpactField.jsx
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Group, Stack, Text, Button, Select, Textarea, Paper } from '@mantine/core';
import { IconEdit, IconCheck, IconX } from '@tabler/icons-react';

/**
 * Normalize value to always be a string
 * Handles: string, array (takes first), null/undefined
 */
const normalizeValue = (val) => {
  if (!val) return '';
  if (Array.isArray(val)) return val[0] || '';
  if (typeof val === 'string') return val;
  return '';
};

/**
 * EditableImpactField - Custom editable field for CIA triad impact editing
 * Combines Select (for impact level) + Textarea (for rationale)
 * Saves both atomically to prevent partial updates
 */
function EditableImpactField({
  label,
  impactKey,
  values = '',
  rationale = '',
  itemType,
  itemId,
  fieldName,
  onSave,
  impactLevelOptions = ['Low', 'Medium', 'High'], // Template-specific options with fallback
}) {
  // Normalize value to always be a string
  const normalizedValue = useMemo(() => normalizeValue(values), [values]);

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(normalizedValue);
  const [editRationale, setEditRationale] = useState(rationale);
  const [saving, setSaving] = useState(false);

  const handleEdit = () => {
    setEditValue(normalizedValue);
    setEditRationale(rationale);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(normalizedValue);
    setEditRationale(rationale);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build the update object with both impact level and rationale
      const impactUpdates = {
        [impactKey]: editValue,
        [`${impactKey}Rationale`]: editRationale,
      };

      await onSave(impactUpdates);
      setIsEditing(false);
    } catch (error) {
      console.error('[EditableImpactField] Error saving impact:', error);
    } finally {
      setSaving(false);
    }
  };

  // Helper: Get color for impact level badge (matches risk rating colors)
  const getImpactColor = (value) => {
    switch (value) {
      case 'Critical':
        return { bg: '#1A1A1A', color: '#FFFFFF' };
      case 'High':
        return { bg: '#993333', color: '#FFFFFF' };
      case 'Significant':
        return { bg: '#B94D4D', color: '#FFFFFF' };
      case 'Moderate':
        return { bg: '#D4AA7D', color: '#000000' };
      case 'Low':
        return { bg: '#B8C2B7', color: '#000000' };
      case 'Negligible':
        return { bg: '#D8D8D8', color: '#000000' };
      case 'None':
        return { bg: '#EBEBEB', color: '#000000' };
      default:
        return { bg: '#f8f9fa', color: '#495057' };
    }
  };

  // Display mode - shows current value with edit icon
  if (!isEditing) {
    const colors = normalizedValue ? getImpactColor(normalizedValue) : null;

    return (
      <Paper
        withBorder
        p="sm"
        onClick={handleEdit}
        style={{
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#228be6',
            backgroundColor: '#f8f9fa',
          },
        }}
      >
        <Group justify="space-between" align="flex-start">
          <Stack gap={4} style={{ flex: 1 }}>
            <Text size="sm" fw={600} c="dimmed">
              {label}
            </Text>
            {normalizedValue ? (
              <Text
                size="sm"
                fw={500}
                px="xs"
                py={2}
                style={{
                  borderRadius: 4,
                  backgroundColor: colors.bg,
                  color: colors.color,
                  display: 'inline-block',
                }}
              >
                {normalizedValue}
              </Text>
            ) : (
              <Text size="sm" c="dimmed" fs="italic">
                Not assessed
              </Text>
            )}
            <Text size="xs" c="dimmed" lineClamp={2}>
              {rationale || 'No rationale provided'}
            </Text>
          </Stack>
          <IconEdit size={16} color="#868e96" />
        </Group>
      </Paper>
    );
  }

  // Edit mode - allows selecting single impact level and editing rationale
  return (
    <Paper withBorder p="sm">
      <Stack gap="sm">
        <Text size="sm" fw={600} c="dimmed">
          {label}
        </Text>

        <Select
          label="Impact Level"
          placeholder="Select impact level"
          value={editValue}
          onChange={setEditValue}
          data={impactLevelOptions}
          clearable
          searchable={false}
        />

        <Textarea
          label="Rationale"
          placeholder="Explain the reasoning for this impact assessment..."
          value={editRationale}
          onChange={(e) => setEditRationale(e.target.value)}
          minRows={3}
          autosize
          maxRows={6}
        />

        <Group gap="xs" justify="flex-end">
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            onClick={handleCancel}
            disabled={saving}
            leftSection={<IconX size={14} />}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            onClick={handleSave}
            loading={saving}
            leftSection={<IconCheck size={14} />}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

EditableImpactField.propTypes = {
  label: PropTypes.string.isRequired,
  impactKey: PropTypes.string.isRequired,
  values: PropTypes.string, // Single value, not array
  rationale: PropTypes.string,
  itemType: PropTypes.string.isRequired,
  itemId: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  impactLevelOptions: PropTypes.arrayOf(PropTypes.string),
};

export default EditableImpactField;
